import { useState } from "react";
import { useAuth } from "@/contexts";
import { signInWithApple, signInWithGoogle, signInWithPassword } from "@/features/auth/api";

export function useSignInForm(onSuccess: () => void) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const { refreshSession, setSession } = useAuth();

  function clearFormError(): void {
    if (formError) setFormError("");
  }

  async function handlePasswordSignIn(): Promise<void> {
    setFormError("");
    if (!email.trim() || !password.trim()) {
      setFormError("Enter email and password.");
      return;
    }
    setSubmitting(true);
    const { data, error } = await signInWithPassword(email.trim(), password);
    setSubmitting(false);
    if (error) {
      setFormError(error.message);
    } else {
      // The sign-in response already includes the verified user, so hydrate
      // auth state from it directly instead of re-checking the new session
      // through refreshSession's `/auth/session` call — that call does its
      // own DB read and can lose a read-after-write race against a session
      // that was created moments earlier, showing a false "session expired"
      // warning right after a successful sign-in.
      if (data?.token && data.user) {
        setSession(data.user, data.token);
      } else {
        await refreshSession();
      }
      onSuccess();
    }
  }

  async function handleGoogleSignIn(): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    const { error } = await signInWithGoogle();
    setSubmitting(false);
    if (error) setFormError(error.message);
  }

  async function handleAppleSignIn(): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    const { error } = await signInWithApple();
    setSubmitting(false);
    if (error) setFormError(error.message);
  }

  return {
    email,
    setEmail,
    password,
    setPassword,
    submitting,
    formError,
    handlePasswordSignIn,
    handleGoogleSignIn,
    handleAppleSignIn,
    clearFormError,
  };
}
